/**
 * useServiceOrderModule - Fonte única da flag do módulo Ordem de Serviço
 *
 * Lê `service_orders_enabled` via RPC `get_service_order_module_settings` e expõe a mutation de
 * ativação (somente administradores, validado no banco).
 *
 * RELEASE: 2026-10-08 v1.0.0 - Criação inicial (Fase 1 da OS).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

const logger = createLogger('useServiceOrderModule');

/** Hook completo (flag + ativação) */
export function useServiceOrderModule() {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['service-order-module', tenantId],
    enabled: !!tenantId,
    staleTime: 60_000,
    queryFn: async () => {
      // `.call(supabase, ...)` preserva o `this` do cliente
      const { data, error } = await (supabase.rpc as any).call(supabase, 'get_service_order_module_settings', {
        p_tenant_hint: tenantId ?? null,
      });
      if (error) {
        logger.error('Erro ao ler flag do módulo Ordem de Serviço', { error: error.message });
        throw error;
      }
      return Boolean((data as { service_orders_enabled?: boolean } | null)?.service_orders_enabled);
    },
  });

  const toggleModule = useMutation({
    mutationFn: async (enabled: boolean) => {
      const { error } = await (supabase.rpc as any).call(supabase, 'update_service_order_module_settings', {
        p_enabled: enabled,
        p_tenant_hint: tenantId ?? null,
      });
      if (error) throw error;
    },
    onSuccess: (_d, enabled) => {
      queryClient.invalidateQueries({ queryKey: ['service-order-module'] });
      toast.success(enabled ? 'Módulo Ordem de Serviço habilitado' : 'Módulo Ordem de Serviço desabilitado');
    },
    onError: (e: Error) => toast.error(e.message || 'Não foi possível alterar o módulo'),
  });

  return { isEnabled: query.data ?? false, isLoading: query.isLoading, toggleModule };
}

/** Atalho usado pelos gates de navegação */
export function useServiceOrderModuleEnabled(): boolean {
  return useServiceOrderModule().isEnabled;
}
